import { ethers } from "ethers";
import {
  getStoredReferrerCode,
  clearStoredReferrerCode,
} from "../utils/referral";
import { API_BASE_URL } from "./api";

export interface LoginResponse {
  token: string;
  walletAddress: string;
  expiresIn: string;
  message: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  walletAddress: string | null;
}

class AuthService {
  private static instance: AuthService;
  private readonly TOKEN_KEY = "deathx-auth-token";
  private readonly WALLET_KEY = "deathx-wallet-address";
  private readonly AUTH_MESSAGE_PREFIX = "DeathX.Fun-Login:";

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Create login message for signing
   */
  createLoginMessage(walletAddress: string, timestamp: number): string {
    return `${this.AUTH_MESSAGE_PREFIX}${walletAddress}:${timestamp}`;
  }

  /**
   * Login with wallet signature
   */
  async login(signer: ethers.JsonRpcSigner): Promise<LoginResponse> {
    try {
      const walletAddress = await signer.getAddress();
      const timestamp = Date.now();
      const message = this.createLoginMessage(walletAddress, timestamp);

      // Sign the message
      const signature = await signer.signMessage(message);

      // Get referrer code if available
      const referrerCode = getStoredReferrerCode();

      // Prepare login payload
      const loginPayload: any = {
        walletAddress,
        signature,
        message,
        timestamp,
      };

      // Add referrer code if available
      if (referrerCode) {
        loginPayload.referrerCode = referrerCode;
      }

      // Send login request to backend
      const response = await fetch(
        `${API_BASE_URL}/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(loginPayload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Login failed");
      }

      const loginData: LoginResponse = await response.json();

      // Store authentication data
      localStorage.setItem(this.TOKEN_KEY, loginData.token);
      localStorage.setItem(this.WALLET_KEY, loginData.walletAddress);

      // Clear referrer code after successful login
      if (referrerCode) {
        clearStoredReferrerCode();
        console.log("Referrer code cleared after successful login");
      }

      return loginData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Logout and clear stored data
   */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.WALLET_KEY);
  }

  /**
   * Get stored authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get stored wallet address
   */
  getWalletAddress(): string | null {
    return localStorage.getItem(this.WALLET_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const walletAddress = this.getWalletAddress();
    return !!(token && walletAddress);
  }

  /**
   * Get current auth state
   */
  getAuthState(): AuthState {
    return {
      isAuthenticated: this.isAuthenticated(),
      token: this.getToken(),
      walletAddress: this.getWalletAddress(),
    };
  }

  /**
   * Get authorization header for API requests
   */
  getAuthHeader(): Record<string, string> {
    const token = this.getToken();
    if (!token) {
      throw new Error("No authentication token available");
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Make authenticated API request
   */
  async authenticatedFetch(
    url: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const token = this.getToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // If we get a 401, the token might be expired
    if (response.status === 401) {
      this.logout();
      throw new Error("Authentication expired. Please login again.");
    }

    return response;
  }
}

export const authService = AuthService.getInstance();
